// @ts-check

import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BlockStack, Box, Button, Grid, InlineStack, Page } from "@shopify/polaris";
import { RefreshIcon } from "@shopify/polaris-icons";
import { keepPreviousData } from "@tanstack/react-query";

import { TopUTMSetsCard } from "components/cards/TopUTMSetsCard/TopUTMSetsCard";
import { MostRecentOrdersCard } from "components/cards/MostRecentOrdersCard/MostRecentOrdersCard";
import { OrdersDatePicker } from "components/OrdersDatePicker/OrdersDatePicker";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";

export default function UTMCampaignPage() {
  const navigate = useNavigate();
  const { dates } = useGlobalContext();
  const { campaign } = useParams();

  const apiUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/utm-campaigns/${encodeURIComponent(campaign)}?${new URLSearchParams(params).toString()}`;
  }, [dates, campaign]);

  const {
    data,
    refetch,
    isRefetching,
    isFetching,
  } = useAppQuery({
    url: apiUrl,
    extraQueryKeys: ["utmSets"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  let utmSetsData = [];
  let utmSetsTotalCount = 0;
  if (!isFetching && data?.utmSets) {
    utmSetsData = data.utmSets;
    utmSetsTotalCount = data.utmSetsTotalCount;
  }

  let ordersData = [];
  let ordersTotalCount = 0;
  if (!isFetching && data?.utmSets) {
    ordersData = data.orders;
    ordersTotalCount = data.ordersTotalCount;
  }

  return (
    <Page
      title="UTM Campaign"
      subtitle={campaign}
      backAction={{
        content: "Back to Dashboard",
        onAction: () => {
          navigate("/");
        },
      }}
      fullWidth
    >
      <BlockStack gap="400">
        <InlineStack gap="200">
          <OrdersDatePicker />

          <Button
            icon={RefreshIcon}
            loading={isFetching}
            onClick={refetch}
          >
            Refresh
          </Button>
        </InlineStack>

        <Grid>
          <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
            <TopUTMSetsCard
              metricName="campaign"
              metricKey="utm_campaign"
              metricValue={campaign}
              data={utmSetsData}
              totalCount={utmSetsTotalCount}
              loading={isFetching}
            />
          </Grid.Cell>
          <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
            <MostRecentOrdersCard
              metricName="campaign"
              metricKey="utm_campaign"
              metricValue={campaign}
              data={ordersData}
              totalCount={ordersTotalCount}
              loading={isFetching}
            />
          </Grid.Cell>
        </Grid>
      </BlockStack>
    </Page>
  );
}
